import React, { useState, useEffect } from "react";
import { createModel } from "hox";
import useStorageState from "@/hooks/useStorageState";
import { globalPrompt } from "@/components/message";
import { dictService, IDictDetail, IDictSearch } from "./dictService";
import { forFind } from "@/utils/utils";
import { DictCodeEnmu } from "@/types/AppDictEnums";
import { ISelectOptions } from "@/types/AppTypes";
import { defaultMdCmParam } from "@/utils/constants";
import { message } from "antd";

type dictObj = {
    [key: string]: any
}

export type ITreeSelectData = {
    title: string,
    value: string,
    selectable?: boolean,
    children: ITreeSelectData[]
}

function listChangeTree(list: IDictDetail[]) {
    let menuObj: dictObj = {}
    list.forEach(item => {
        item.children = []
        menuObj[item.code] = item
    })
    let filterList = list.filter(item => {
        if (!item.pcode) {
            item.pcode = null;
        }
        if (item.pcode !== null && item.pcode !== "") {
            menuObj[item.pcode]?.children.push(item)
            return false
        }
        return true
    });
    return filterList
}

export const useDictModel = createModel(function () {
    const { sourceApp, branch } = defaultMdCmParam;
    const [treeSource, setTreeSource, clearTreeSource] = useStorageState<IDictDetail[]>("dictTree", []);  //字典数据源
    const [originDictList, setOriginDictList, clearOriginDictList] = useStorageState<IDictDetail[]>("dictList", []);  //字典数据源
    const [selectedNodes, setSelectedNodes] = useState<IDictDetail[]>([]);   //树选中的行信息
    const [mode, setMode] = useState<"add" | "nextAdd" | "edit" | "root" | "">("");  //模式
    const [formVal, setFormVal] = useState<IDictDetail>(selectedNodes[0]);  //表单缓存

    //实时缓存
    const onChangeFormVal = (changedValues: { [key: string]: any }) => {
        setFormVal({ ...formVal, ...changedValues });
    }

    //字典初始
    const initDictFormFields = {
        pcode: "",
        code: "",
        name: "",
        nameEn: "",
        note: "",
        ver: 0,
        sourceAuthKey: "",
        children: [],
        enable: 0,
        branch,
        value: "",
        key: ""
    }

    useEffect(() => {
        if (mode === "add") {
            setFormVal({ ...selectedNodes[0], ...initDictFormFields, pcode: selectedNodes[0].pcode })
        } else if (mode === "nextAdd") {
            setFormVal({ ...selectedNodes[0], ...initDictFormFields, pcode: selectedNodes[0].code })
        } else if (mode === "root") {
            setSelectedNodes([initDictFormFields])
            setFormVal(initDictFormFields)
        }
    }, [mode])

    //字典列表请求
    const loadData = async () => {
        let data: IDictSearch = {
            sourceApp,
            page: {
                pageSize: -1,
                pageNum: 1,
            },
        }
        let { retData } = await dictService.page(data);
        let { records } = retData;
        setOriginDictList(records);
        setTreeSource(listChangeTree(records.filter(item => item.enable === 1)));
    }

    const sync = async () => {
        let { retData } = await dictService.sync({
            sourceApp: defaultMdCmParam.sourceApp,
            // branch: "main",
            branchList: ["gtm", "main"]
        });
        if (retData) {
            loadData();
            globalPrompt("message", { type: "success", text: "同步成功。。。" });
        }
    }
    //表单提交
    const onSubmit = async () => {
        let { code, pcode, note, name, branch, value, nameEn } = formVal;
        let data = {
            sourceApp, branch, value, nameEn,
            code, pcode, note, name
        }
        if (branch !== defaultMdCmParam.branch) {
            message.warning("您无权修改main公用分支的字典")
            return;
        }

        if (!data.code || !data.name) {
            globalPrompt("message", { text: "请检查必填项", type: "warning" });
            return;
        }
        if (mode === "add" || mode === "nextAdd" || mode === "root") {
            let { retData } = await dictService.insert(data);
            retData && globalPrompt("message", { type: "success", text: "字典增加成功" });
            loadData();
            setSelectedNodes([]);
        } else if (mode === "edit") {
            let { retData } = await dictService.update(data);
            retData && globalPrompt("message", { type: "success", text: "字典编辑成功" });
            loadData();
            setSelectedNodes([]);
        }
    }

    //删除一条数据
    const removeOneDict = async (code: string) => {
        let data = {
            sourceApp, branch,
            code
        }
        let { retData } = await dictService.remove(data);
        retData && globalPrompt("message", { type: "success", text: "字典删除成功" });
        loadData();
        setSelectedNodes([]);
    }

    //根据code返回 select框的options 格式；
    const getSelectedDictOne = (code: DictCodeEnmu): ISelectOptions[] => {
        let findTree: IDictDetail | undefined = forFind(treeSource, code, "code");
        return findTree ? findTree.children.map(item => ({ label: item.name, value: item.code, type: item.value ?? "" })) : []
    }
    //根据code返回 select框的options 格式；
    const getSelectedDictValueOne = (code: DictCodeEnmu): ISelectOptions[] => {
        let findTree: IDictDetail | undefined = forFind(treeSource, code, "code");
        return findTree ? findTree.children.map(item => ({ label: item.value ? item.value : item.name, value: item.code, type: item.value ?? "" })) : []
    }

    //根据code 返回对应的node节点名称
    const getTreeNodeName = (code?: string): string => {
        if (!code) return code ?? "";
        let findNode: IDictDetail | undefined = originDictList.find(item => item.code === code);
        return findNode ? findNode["name"] : code ?? "";
    }
    //根据code 返回对应的node节点英文名称
    const getTreeNodeNameEn = (code?: string): string => {
        if (!code) return code ?? "";
        let findNode: IDictDetail | undefined = originDictList.find(item => item.code === code);
        return findNode ? findNode.nameEn ?? "" : code ?? "";
    }

    //根据code 返回对应的value 字典值 （例如， 币种的符号）
    const getTreeNodeValue = (code?: string): string => {
        if (!code) return "";
        let findNode: IDictDetail | undefined = originDictList.find(item => item.code === code);
        return findNode ? findNode["value"] ?? "" : "";
    }
    //根据code 返回对应的value 字典值 （例如， 币种的符号）
    const getTreeNodeNameValue = (name?: string): string => {
        if (!name) return "";
        let findNode: IDictDetail | undefined = originDictList.find(item => item.name === name);
        return findNode ? findNode["value"] ?? "" : "";
    }

    //根据code 返回对应树节点
    const getTreeNodeOne = (code: DictCodeEnmu): IDictDetail[] => {
        let findTree: IDictDetail | undefined = forFind(treeSource, code, "code");
        return findTree ? [findTree] : [];
    }


    const mapTree = (org: IDictDetail, selectable: boolean): ITreeSelectData => {
        const haveChildren = Array.isArray(org.children) && org.children.length > 0;
        return {
            title: org.name,
            value: org.code,
            selectable: selectable ? true : haveChildren ? false : true,
            //判断它是否存在子集，若果存在就进行再次进行遍历操作，知道不存在子集便对其他的元素进行操作
            children: haveChildren ? org.children.map(i => mapTree(i, selectable)) : []
        };
    }

    /**
     * 
     * @param code 根据code 返回treeSelect 数据格式
     * @param selectable 有子节点的父节点不能选中  (默认只能选中子节点)
     */
    const getTreeSelectOne = (code: DictCodeEnmu, selectable: boolean = false): ITreeSelectData[] => {
        let findTree: IDictDetail | undefined = forFind(treeSource, code, "code");
        let resultTree: ITreeSelectData[] = findTree ? mapTree(findTree, selectable).children : [];
        return resultTree;
    }

    return {
        /***field***/
        treeSource,
        selectedNodes,
        mode,
        formVal,
        /***method***/
        setTreeSource,
        setSelectedNodes,
        setMode,
        setFormVal,
        onChangeFormVal,
        loadData,
        onSubmit,
        removeOneDict,
        getSelectedDictOne,
        getTreeNodeName,
        getTreeNodeOne,
        getTreeSelectOne, sync,
        getTreeNodeValue,
        getTreeNodeNameValue,
        getSelectedDictValueOne,
        getTreeNodeNameEn
    }
});